import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'

// Algorithme de Dantzig pour trouver le chemin minimal
export const runDantzigMin = (
    nodes, edges, startNode, endNode, setPath, setSteps,
    setShowSteps, setAfficheMin
) => {
  setAfficheMin(true)

    if (nodes.length < 2) {
      toast.error("Veuillez créer au moins deux sommets", {
        position: 'top-right',
        autoClose: 2000,
        closeButton: false,
        hideProgressBar: true,
        transitionDuration: 0,
        theme: 'colored',
        icon: <HighlightOffIcon style={{ color: 'white' }} />
      })
      return
    }
    
    if (edges.length === 0) {
      toast.error("Veuillez créer au moins une arête", {
        position: 'top-right',
        autoClose: 2000,
        closeButton: false,
        hideProgressBar: true,
        transitionDuration: 0,
        theme: 'colored',
        icon: <HighlightOffIcon style={{ color: 'white' }} />
      })
      return
    }
    
    if (!startNode || !endNode) {
      toast.error("Veuillez sélectionner un sommet de départ et d'arrivée", {
        position: 'top-right',
        autoClose: 2000,
        closeButton: false,
        hideProgressBar: true,
        transitionDuration: 0,
        theme: 'colored',
        icon: <HighlightOffIcon style={{ color: 'white' }} />
      })
      return
    }
    
    // Créer un graphe adapté à l'algorithme
    const graph = {}
    nodes.forEach(node => {
      graph[node.id] = { name: node.name, connections: {} }
    })
    
    edges.forEach(edge => {
      graph[edge.source].connections[edge.target] = edge.weight
      // Pour un graphe non orienté, décommenter la ligne suivante:
      // graph[edge.target].connections[edge.source] = edge.weight
    })
    
    // Algorithme de Dantzig
    const lambda = {}
    const markedNodes = new Set()
    const predecessors = {}
    const stepsList = []
    
    // Initialiser
    lambda[startNode.id] = 0
    markedNodes.add(startNode.id)
    
    // Ajouter l'étape initiale
    stepsList.push({
      step: 0,
      text: `Initialisation: λ(${startNode.name}) = 0`,
      markedNodes: new Set([startNode.id]),
      lambda: { ...lambda },
      connections: [], // Nouvelle propriété pour stocker les connexions évaluées
      ensembleE: `E₁ = {${startNode.name}}`, // Ensemble E formaté
      startNodeName: startNode.name // Stocke le nom du sommet de départ
    })
    
    // Remplacer la section de l'algorithme principal dans runDantzigMin
// À partir de la ligne "let k = 1; // Compteur d'étapes"

let k = 1; // Compteur d'étapes
let allNodesProcessed = false;

while (!allNodesProcessed) {
  let minLambda = Infinity
  let nodeToMark = null
  let prevNode = null
  let connectionsEvaluated = [] // Pour stocker toutes les connexions évaluées à cette étape
  
  // Pour chaque sommet marqué
  markedNodes.forEach(markedNodeId => {
    const markedNode = graph[markedNodeId]
    
    // Vérifier toutes ses connexions
    Object.entries(markedNode.connections).forEach(([targetId, weight]) => {
      if (!markedNodes.has(parseInt(targetId))) {
        const targetNodeName = nodes.find(n => n.id === parseInt(targetId)).name
        const markedNodeName = nodes.find(n => n.id === markedNodeId).name
        const newLambda = lambda[markedNodeId] + weight
        
        // Ajouter cette connexion évaluée
        connectionsEvaluated.push({
          from: markedNodeId,
          to: parseInt(targetId),
          fromName: markedNodeName,
          toName: targetNodeName,
          weight: weight,
          calculatedLambda: newLambda
        })
        
        // Utiliser ce chemin s'il est meilleur
        if (newLambda < (lambda[targetId] || Infinity)) {
          lambda[targetId] = newLambda
          predecessors[targetId] = markedNodeId
        }
      }
    })
  })
  
  // Trouver le sommet avec la valeur lambda minimale
  Object.entries(lambda).forEach(([nodeId, value]) => {
    if (!markedNodes.has(parseInt(nodeId)) && value < minLambda) {
      minLambda = value
      nodeToMark = parseInt(nodeId)
    }
  })
  
  // Si aucun sommet à marquer n'est trouvé, tous les sommets accessibles ont été traités
  if (nodeToMark === null) {
    allNodesProcessed = true
    break
  }
  
  // Marquer le sommet
  markedNodes.add(nodeToMark)
  prevNode = predecessors[nodeToMark]
  
  // Ajouter l'étape avec un format amélioré
  const nodeName = nodes.find(n => n.id === nodeToMark).name
  
  // Construire la liste des nœuds marqués pour l'ensemble E
  const markedNodeNames = Array.from(markedNodes).map(id => 
    nodes.find(n => n.id === id).name
  ).join(', ')
  
  // Ajouter l'étape formatée
  stepsList.push({
    step: k,
    connections: connectionsEvaluated,
    markedNodes: new Set([...markedNodes]),
    lambda: { ...lambda },
    nodeMarked: nodeName,
    minLambda: minLambda,
    ensembleE: `E${k+1} = {${markedNodeNames}}`
  })
  
  k++
}

// Vérifier s'il existe un chemin vers le sommet d'arrivée
if (!lambda[endNode.id] || lambda[endNode.id] === Infinity) {
  toast.error("Impossible de trouver un chemin entre les sommets sélectionnés", {
    position: 'top-right',
    autoClose: 2000,
    closeButton: false,
    hideProgressBar: true,
    transitionDuration: 0,
    theme: 'colored',
    icon: <HighlightOffIcon style={{ color: 'white' }} />
  })
  return
}

// Fonction pour trouver tous les chemins minimaux
const findAllMinimalPaths = (endNodeId, startNodeId, lambda, graph) => {
  const allPaths = []
  
  const findPaths = (currentId, path, targetDistance) => {
    if (currentId === startNodeId) {
      allPaths.push([...path].reverse())
      return
    }
    
    // Chercher tous les prédécesseurs possibles
    Object.entries(graph).forEach(([nodeId, nodeData]) => {
      const fromId = parseInt(nodeId)
      if (nodeData.connections[currentId] && 
          lambda[fromId] + nodeData.connections[currentId] === targetDistance) {
        findPaths(fromId, [...path, fromId], lambda[fromId])
      }
    })
  }
  
  findPaths(endNodeId, [endNodeId], lambda[endNodeId])
  return allPaths
}

// Trouver tous les chemins minimaux
const allMinimalPaths = findAllMinimalPaths(endNode.id, startNode.id, lambda, graph)

// Convertir les IDs en objets nodes pour tous les chemins
const allPathNodes = allMinimalPaths.map(pathIds => 
  pathIds.map(id => nodes.find(node => node.id === id))
)

// Utiliser le premier chemin pour l'affichage principal
const pathNodes = allPathNodes[0]

setPath(pathNodes)
setSteps(stepsList)
setShowSteps(true)

// Stocker tous les chemins pour l'affichage
stepsList.allMinimalPaths = allPathNodes

// Ajouter les informations des chemins multiples aux étapes
const lastStep = stepsList[stepsList.length - 1]
lastStep.allMinimalPaths = allPathNodes
lastStep.hasMultiplePaths = allPathNodes.length > 1

}